"use client";

import { type ComponentPropsWithRef, useEffect, useRef } from "react";

import { runInAction } from "mobx";
import { useTranslations } from "next-intl";

import { type FormFieldModel } from "../FormFieldModel";

import { type FormRegister } from "./useForm";

type FormFieldResult = {
  inputProps: ComponentPropsWithRef<"input">;
  errorMessage: string | null;
};

export function useFormField<T extends string>(
  fieldName: T,
  model: FormFieldModel<T>,
  register: FormRegister,
  inputProps: ComponentPropsWithRef<"input"> = {},
): FormFieldResult {
  const ref = useRef<HTMLInputElement>(null);
  const t = useTranslations();

  useEffect(() => {
    register(fieldName, {
      ref,
      validate: () => {
        const formField = model.formFields[fieldName];
        const error = formField.validate?.(formField.value) ?? null;
        runInAction(() => {
          formField.error = error ? t(error) : null;
        });
        return error;
      },
      getValue: () => model.formFields[fieldName].value,
    });

    return () => {
      runInAction(() => {
        model.formFields[fieldName].error = null;
      });
      register(fieldName, undefined);
    };
  }, [model, fieldName, register, t]);

  const { error, value } = model.formFields[fieldName];

  return {
    inputProps: {
      ...inputProps,
      ref,
      defaultValue: inputProps.defaultValue ?? value,
      name: fieldName,
      "aria-invalid": error ? "true" : "false",
      onChange: (e) => {
        runInAction(() => {
          model.formFields[fieldName].value = e.target.value;
          model.formFields[fieldName].error = null;
        });
        if (inputProps.onChange) {
          inputProps.onChange(e);
        }
      },
      onFocus: () => {
        runInAction(() => {
          model.formFields[fieldName].focused = true;
        });
      },
      onBlur: () => {
        runInAction(() => {
          model.formFields[fieldName].focused = false;
        });
      },
    },
    errorMessage: error,
  };
}
